import { useRef, useState } from 'preact/hooks';
import { verifyEmail, type EmailVerdict } from '@/api/client';
import { suggestEmailCorrection } from '@/email';

export interface EmailVerification {
  // The address a warn verdict was shown for, or null. The host renders the
  // inline notice while this is set; any edit clears it (the re-verify on the
  // next blur warns again if the new address is also bad).
  warnedFor: string | null;
  // Feed the Stripe email element's change event (Contact Details Element on
  // the expedited card path, Link Authentication Element on the trial path).
  // Tracks the current address and clears state on edit — never fires a
  // verify, so typing (and pause-typing) costs no vendor calls and never
  // flashes the warning mid-edit.
  onEmailChange: (email: string | null, complete: boolean) => void;
  // Fire on the element's blur: verify the complete address in the field.
  // Blur is the trigger — the visitor has finished the email and moved to the
  // card, so the verdict lands while they type it.
  onEmailBlur: () => void;
  // Start a verify for the address without surfacing it (deduped per address).
  // For hosts that show the verdict later, at their own gate.
  prefetch: (email: string) => void;
  // The address's verdict, starting a verify if none exists. Resolves 'ok' if
  // capMs elapses first (fail open); the lookup keeps running either way.
  verdictWithin: (email: string, capMs: number) => Promise<EmailVerdict>;
}

// useEmailVerification is the deliverability gate for the deferred-identity
// payment forms, where Stripe's email element (not our EmailStep) collects the
// address: verify on blur, surface a warn as an inline notice. Advisory by
// design — deliverability never blocks a checkout, so there is no confirm-time
// verdict await and no hard verdict. verifyEmail itself fails open, so every
// path here resolves — nothing throws. The guest EmailStep instead prefetches
// on a typing pause and awaits the verdict, bounded, at submit.
export function useEmailVerification(): EmailVerification {
  const [warnedFor, setWarnedFor] = useState<string | null>(null);
  // The in-flight/settled verify for an address, and the complete address
  // currently in the field awaiting a blur. Refs only: the element's change/
  // blur handlers are bound once inside the Stripe slot effects, so these
  // callbacks must never close over state.
  const currentRef = useRef<{ email: string; promise: Promise<EmailVerdict> } | null>(null);
  const pendingRef = useRef<string | null>(null);

  const start = (email: string): Promise<EmailVerdict> => {
    if (currentRef.current?.email === email) {
      return currentRef.current.promise;
    }
    const promise = verifyEmail(email);
    currentRef.current = { email, promise };
    return promise;
  };

  const onEmailChange = (email: string | null, complete: boolean) => {
    const trimmed = (email ?? '').trim();
    pendingRef.current = complete && trimmed ? trimmed : null;
    if (currentRef.current && currentRef.current.email !== trimmed) {
      // Edited away from the verified address — its verdict no longer applies.
      currentRef.current = null;
    }
    // Functional update: this handler's closure is bound once at element
    // mount, so a direct read of warnedFor would be stale.
    setWarnedFor((prev) => (prev !== null && prev !== trimmed ? null : prev));
  };

  const onEmailBlur = () => {
    const email = pendingRef.current;
    if (!email || currentRef.current?.email === email) {
      return;
    }
    // An address with an obvious typo fix needs no vendor call: Stripe's
    // email element is already showing its own (non-disableable) "Did you
    // mean?" hint, which names the fix — the hint is the intervention.
    if (suggestEmailCorrection(email)) {
      return;
    }
    void start(email).then((verdict) => {
      if (currentRef.current?.email === email && verdict === 'warn') {
        setWarnedFor(email);
      }
    });
  };

  const prefetch = (email: string) => {
    void start(email);
  };

  const verdictWithin = (email: string, capMs: number) => {
    const verdict = start(email);
    return new Promise<EmailVerdict>((resolve) => {
      const timer = setTimeout(() => resolve('ok'), capMs);
      void verdict.then((v) => {
        clearTimeout(timer);
        resolve(v);
      });
    });
  };

  return { warnedFor, onEmailChange, onEmailBlur, prefetch, verdictWithin };
}
